import { capturePortalLocaleRequest, resolvePortalLocaleRequest, failPortalLocaleRequest } from '@/i18n/portalLocaleStore.js';
// src/api/clubSession.js
import { apiGet } from '@/api/apiClient';
import { normalizeDashboardMode } from '../../shared/dashboardModes.mjs';

function normalizeClubCode(value) {
  if (!value) return null;
  const str = String(value).trim();
  if (!/^[0-9]{6}$/.test(str)) return str || null;
  return str;
}

async function requestClubUserSession(clubCode) {
  const code = normalizeClubCode(clubCode);
  if (!code) {
    throw new Error('club code required');
  }

  const path = `/api/clubs/${encodeURIComponent(code)}/me`;
  const res = await apiGet(path, { credentials: 'include', timeout: 15000 });
  const payload = res?.data ?? res ?? {};

  if (!res?.ok) {
    const error = new Error(payload?.error || res?.error || 'club access denied');
    error.status = res?.status;
    error.response = payload;
    if (payload?.reason) {
      error.reason = payload.reason;
    }
    throw error;
  }

  const clubUser = payload?.club_user ?? payload ?? {};
  const normalizedClubUser = {
    ...clubUser,
    staff_id: clubUser?.staff_id ?? clubUser?.id ?? null,
    club_code: clubUser?.club_code ?? code,
    dashboard_mode: normalizeDashboardMode(clubUser?.dashboard_mode),
  };

  return {
    club_user: normalizedClubUser,
    staff_profile: payload?.staff_profile ?? null,
    clubs: Array.isArray(payload?.clubs) ? payload.clubs : [],
    modules: Array.isArray(payload?.modules) ? payload.modules : [],
    tab_permissions: payload?.tab_permissions ?? { modules: [], route_permissions: [] },
  };
}

export default fetchClubUserSession;

// Deduplicate concurrent bootstrap/profile consumers only within the live SDK scope.
// No successful session or permission result is persisted as an authentication hint.
const pendingSessions = new Map();
export function fetchClubUserSession(clubCode) {
  const ticket = capturePortalLocaleRequest(clubCode);
  const key = ticket ? JSON.stringify([ticket.scope, ticket.generation, ticket.revision]) : null;
  if (key && pendingSessions.has(key)) return pendingSessions.get(key);
  const request = requestClubUserSession(clubCode).then(session => {
    resolvePortalLocaleRequest(ticket, session.club_user);
    return session;
  }, error => {
    failPortalLocaleRequest(ticket);
    throw error;
  }).finally(() => { if (key && pendingSessions.get(key) === request) pendingSessions.delete(key); });
  if (key) pendingSessions.set(key, request);
  return request;
}
